import { useEffect, useRef, useState } from "react";
import { Button, Drawer, message } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  normalizeClassifications,
  type Classifications,
  type DsmScopeItem
} from "design-scope";
import {
  DESIGN_SCOPE_FEATURE_TYPE_ID,
  updateDesignScopeInstances
} from "services/design-scope-apis";
import { sharedClassification } from "../shared/bulk-edit-classification-merge";
import { toBulkEditClassificationUpdateInstance } from "../shared/design-scope-create-mappers";
import { DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE } from "../shared/design-scope-messages";
import BulkSelectionViewPanel from "./bulk-selection-view-panel";
import ClassificationPicker from "./classification-picker";
import { Section } from "./scope-item-field";
import "./scope-item.scss";

export type BulkEditClassificationDrawerProps = {
  open: boolean;
  selectedRows: DsmScopeItem[];
  projectToken: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

type ReplaceSlot = "uf" | "mf" | "label";

export default function BulkEditClassificationDrawer(
  props: BulkEditClassificationDrawerProps
) {
  const { open, selectedRows, projectToken, onClose, onSaved } = props;

  const [classification, setClassification] = useState<
    Classifications | undefined
  >(undefined);
  const [replaceUf, setReplaceUf] = useState(false);
  const [replaceMf, setReplaceMf] = useState(false);
  const [replaceCustom, setReplaceCustom] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  /** Snapshot of picker values when the drawer opened — restored on uncheck. */
  const initialClassificationRef = useRef<Classifications | undefined>(
    undefined
  );

  const selectionKey = selectedRows.map((r) => r.id).join(",");

  useEffect(() => {
    if (!open) return;
    if (selectedRows.length === 0) {
      onClose();
      return;
    }
    // Prefill shared classification when every selected row matches; fields
    // stay disabled until Replace. Uncheck restores this snapshot.
    const shared = sharedClassification(selectedRows);
    initialClassificationRef.current = shared;
    setClassification(shared);
    setReplaceUf(false);
    setReplaceMf(false);
    setReplaceCustom(false);
    // Reset when the drawer opens or the selection identity changes — not on
    // every parent re-render of the selectedRows array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionKey covers row ids
  }, [open, selectionKey, onClose]);

  const ufOk = !replaceUf || Boolean(classification?.uf?.code?.trim());
  const mfOk = !replaceMf || Boolean(classification?.mf?.code?.trim());
  const customOk =
    !replaceCustom || Boolean(classification?.label?.title?.trim());
  const canSave =
    selectedRows.length > 0 &&
    customOk &&
    (replaceCustom ? true : (replaceUf || replaceMf) && ufOk && mfOk);

  const resetSlotsToInitial = (slots: ReplaceSlot[]) => {
    const initial = initialClassificationRef.current;
    setClassification((prev) =>
      normalizeClassifications({
        uf: slots.includes("uf") ? initial?.uf : prev?.uf,
        mf: slots.includes("mf") ? initial?.mf : prev?.mf,
        label: slots.includes("label") ? initial?.label : prev?.label
      })
    );
  };

  const onReplaceUfChange = (checked: boolean) => {
    setReplaceUf(checked);
    if (checked) {
      setReplaceCustom(false);
      resetSlotsToInitial(["label"]);
      return;
    }
    resetSlotsToInitial(["uf"]);
  };

  const onReplaceMfChange = (checked: boolean) => {
    setReplaceMf(checked);
    if (checked) {
      setReplaceCustom(false);
      resetSlotsToInitial(["label"]);
      return;
    }
    resetSlotsToInitial(["mf"]);
  };

  const onReplaceCustomChange = (checked: boolean) => {
    setReplaceCustom(checked);
    if (checked) {
      setReplaceUf(false);
      setReplaceMf(false);
      resetSlotsToInitial(["uf", "mf"]);
      return;
    }
    resetSlotsToInitial(["label"]);
  };

  const onSave = async () => {
    if (!canSave || inProgress) return;
    if (!projectToken) {
      message.error("Project auth is not ready. Please try again.");
      return;
    }
    setInProgress(true);
    try {
      const feature_instances = selectedRows.map((row) =>
        toBulkEditClassificationUpdateInstance(
          row.id,
          classification,
          row.commitments,
          row.bids,
          {
            replaceUf,
            replaceMf,
            replaceCustom,
            existing: row.classification
          }
        )
      );
      const response = await updateDesignScopeInstances(
        {
          feature_type: "DESIGN_SCOPE",
          feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
          feature_instances
        },
        projectToken
      );
      if (!response.success) {
        message.error(response.errorMsg || "Unable to update classification.");
        return;
      }
      message.success(DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE);
      await onSaved();
    } catch (err) {
      console.error(err);
      message.error("Unable to update classification.");
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (!inProgress) onClose();
      }}
      size={560}
      closable={false}
      mask={{ closable: false }}
      destroyOnHidden
      className="dsm-edit"
      title={
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            data-testid="scope-bulk-edit-classification-back"
            className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-ciq-primary-900"
            onClick={() => {
              if (!inProgress) onClose();
            }}
          >
            <ArrowLeftOutlined />
          </button>
          <span className="text-base font-medium text-black text-opacity-90">
            Bulk Edit Classification
          </span>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            disabled={inProgress}
            data-testid="scope-bulk-edit-classification-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!canSave || inProgress}
            loading={inProgress}
            onClick={() => void onSave()}
            data-testid="scope-bulk-edit-classification-submit"
          >
            Save
          </Button>
        </div>
      }
    >
      <div data-testid="scope-bulk-edit-classification-drawer">
        <div className="mb-4 flex items-start gap-2 text-sm text-black text-opacity-50">
          <InfoCircleOutlined className="mt-0.5 shrink-0" />
          <span>
            All selected Design Scope items will have updating to classification
            code.
          </span>
        </div>

        <BulkSelectionViewPanel
          items={selectedRows}
          data-testid="scope-bulk-edit-classification"
        />

        <Section title="Classification Format">
          <ClassificationPicker
            value={classification}
            onChange={setClassification}
            bulkReplace={{
              replaceUf,
              replaceMf,
              replaceCustom,
              onReplaceUfChange,
              onReplaceMfChange,
              onReplaceCustomChange
            }}
          />
        </Section>
      </div>
    </Drawer>
  );
}
